import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import {
  CartesiaVoicePersona,
  ElevenLabsVoicePersona,
  OPENAI_REALTIME_COMPATIBLE_VOICES,
  OpenAiStandardVoicePersona,
  VoiceCallBackgroundSound,
  VoiceCallIdleMessage,
  VoiceCallLanguage,
  VoiceCallModel,
  VoiceCallOptionMode,
  VoiceProvider,
  isCartesiaCompatibleVoicePersona,
  isElevenLabsCompatibleVoicePersona,
  isRealTimeModel,
  isRealtimeCompatibleVoicePersona,
} from 'flow-schema';
import _ from 'lodash';
import { useId, useState } from 'react';

import { Alert, ExternalLink, Text, api, isDefined, isTruthy } from 'common';

import {
  MODEL_HELPER_TEXT,
  MODEL_LABEL,
} from '../../../../../../../VoiceAssistants/ModelTab/constants';
import {
  BACKGROUND_DENOISING_HELPER_TEXT,
  BACKGROUND_DENOISING_LABEL,
  BACKGROUND_SOUND_HELPER_TEXT,
  BACKGROUND_SOUND_LABEL,
  CARTESIA_VOICE_SPEED_HELPER_TEXT,
  ELEVEN_LABS_VOICE_SIMILARITY_BOOST_HELPER_TEXT,
  ELEVEN_LABS_VOICE_SPEED_HELPER_TEXT,
  ELEVEN_LABS_VOICE_STABILITY_HELPER_TEXT,
  EMOTION_RECOGNITION_HELPER_TEXT,
  EMOTION_RECOGNITION_LABEL,
  END_CALL_FUNCTION_HELPER_TEXT,
  END_CALL_FUNCTION_LABEL,
  END_CALL_MESSAGE_HELPER_TEXT,
  END_CALL_MESSAGE_LABEL,
  IDLE_MESSAGES_HELPER_TEXT,
  IDLE_MESSAGES_LABEL,
  IDLE_MESSAGE_MAX_SPOKEN_COUNT_HELPER_TEXT,
  IDLE_MESSAGE_MAX_SPOKEN_COUNT_LABEL,
  IDLE_TIMEOUT_SECONDS_HELPER_TEXT,
  IDLE_TIMEOUT_SECONDS_LABEL,
  LANGUAGE_HELPER_TEXT,
  LANGUAGE_LABEL,
  MAX_CALL_DURATION_HELPER_TEXT,
  MULTILINGUAL_HELPER_TEXT,
  MULTILINGUAL_LABEL,
  SSML_PARSING_HELPER_TEXT,
  START_SPEAKING_SMART_ENDPOINTING_HELPER_TEXT,
  START_SPEAKING_SMART_ENDPOINTING_LABEL,
  START_SPEAKING_WAIT_SECONDS_HELPER_TEXT,
  START_SPEAKING_WAIT_SECONDS_LABEL,
  STOP_SPEAKING_NUMBER_OF_WORDS_HELPER_TEXT,
  STOP_SPEAKING_NUMBER_OF_WORDS_LABEL,
  STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_HELPER_TEXT,
  STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_LABEL,
  STOP_SPEAKING_VOICE_SECONDS_HELPER_TEXT,
  STOP_SPEAKING_VOICE_SECONDS_LABEL,
  VOICE_PERSONA_HELPER_TEXT,
  VOICE_PERSONA_LABEL,
  VOICE_SIMILARITY_BOOST_LABEL,
  VOICE_SPEED_LABEL,
  VOICE_STABILITY_LABEL,
} from '../../../../../../../VoiceAssistants/VoiceTab/constants';
import { BlockAutocomplete } from '../../../ConfigFields/Autocomplete';
import { BlockValueTemplateFieldArea } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../../ConfigFields/Duration';
import { BlockSelectField } from '../../../ConfigFields/SelectField';
import { BlockSwitchField } from '../../../ConfigFields/Switch';
import { BlockTextField } from '../../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';
import { TEMPERATURE_HELPER_TEXT } from '../../Llm/Shared/constants';
import { VoicePersonaDescriptions } from '../descriptionMappings';

const { DurationUnits, VoiceCallModelDescriptions } = api.FlowDefinition;

const TIMEOUT_HELPER_TEXT = `Maximum time to wait for the call to complete, including time spent in queue. After this duration, this step will be moved to the Failed state.`;
const VOICEMAIL_MESSAGE_HELPER_TEXT = `If the callee doesn't pick up the call, the assistant will leave this message.`;

export function AssistantVoiceFormFields(props: {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}) {
  const { expanded, onChange } = props;
  const id = useId();

  const { value: selectedLanguage, setValue: setSelectedLanguage } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlockConfig['language']>('config.language');

  const [previousLanguage, setPreviousLanguage] = useState<VoiceCallLanguage | undefined>(
    selectedLanguage !== VoiceCallLanguage.Multilingual ? selectedLanguage : undefined
  );

  const { value: voiceProvider, setValue: setVoiceProvider } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlockConfig['voiceProvider']>(
      'config.voiceProvider'
    );
  const { value: voicePersona, setValue: setVoicePersona } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlockConfig['voicePersona']>(
      'config.voicePersona'
    );

  const { value: modelTemperature, setValue: setModelTemperature } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlockConfig['modelTemperature']>(
      'config.modelTemperature'
    );

  // Prepare language options
  const languageOptions = Object.entries(VoiceCallLanguage).map(
    ([key, value]: [string, string]) => ({
      value,
      label: key,
    })
  );

  const { value: modelName } =
    useSelectedNodeField<api.FlowDefinition.VoiceCallBlockConfig['modelName']>('config.modelName');

  const [oldModelName] = useState(modelName);

  const isRealtimeModel = modelName ? isRealTimeModel(modelName) : false;

  const availableModels = Object.values(VoiceCallModel);

  const personaOptions =
    voiceProvider === VoiceProvider.OpenAi
      ? isRealtimeModel
        ? OPENAI_REALTIME_COMPATIBLE_VOICES
        : Object.values(OpenAiStandardVoicePersona)
      : voiceProvider === VoiceProvider.Cartesia
        ? Object.values(CartesiaVoicePersona)
        : Object.values(ElevenLabsVoicePersona);

  // Determine if the current provider is natively multilingual
  const isNativelyMultilingual = isDefined(voiceProvider)
    ? isRealtimeModel || voiceProvider === VoiceProvider.Cartesia
    : false;

  // Prepare persona options filtered by selected language
  // For natively multilingual providers, don't filter by language as all voices support all languages
  // For English-only providers (OpenAI Standard), always show all personas (they only support English anyway)
  const isEnglishOnly =
    isDefined(voiceProvider) && voiceProvider === VoiceProvider.OpenAi && !isRealtimeModel;
  const personaOptionsFilteredByLanguage = personaOptions
    .map((persona) => [persona, VoicePersonaDescriptions[persona]] as const)
    .filter(([, voicePersonaDescription]) => {
      // For natively multilingual providers, show all voices regardless of language selection
      if (isNativelyMultilingual) {
        return true;
      }
      if (isEnglishOnly) {
        return true;
      }
      // For ElevenLabs, filter by language
      return !selectedLanguage ||
        !Object.values(VoicePersonaDescriptions).some((persona) =>
          persona.languages?.includes(selectedLanguage)
        )
        ? voicePersonaDescription.languages?.includes(VoiceCallLanguage.English)
        : voicePersonaDescription.languages?.includes(selectedLanguage);
    })
    .map(([voicePersona, voicePersonaDescription]) => ({
      value: voicePersona,
      label: `${voicePersonaDescription.persona} (${voicePersonaDescription.description})`,
    }));

  const isGpt5Model =
    isTruthy(modelName) &&
    [VoiceCallModel.gpt_5, VoiceCallModel.gpt_5_mini, VoiceCallModel.gpt_5_nano].includes(
      modelName
    );

  return (
    <Accordion variant="elevation" expanded={expanded} onChange={onChange}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={id}
        aria-controls="voice-assistant-settings-content"
      >
        <Text variant="heading5">Voice assistant settings</Text>
      </AccordionSummary>
      <AccordionDetails sx={{ gap: 2 }}>
        <Stack spacing={2}>
          <BlockSelectField
            fieldName="config.modelName"
            label={MODEL_LABEL}
            helperText={MODEL_HELPER_TEXT}
            options={availableModels.map((value) => ({
              value,
              label: VoiceCallModelDescriptions[value],
            }))}
            onChange={(event) => {
              const isRealtimeModelAfterChange = event.target.value
                ? isRealTimeModel(event.target.value as VoiceCallModel)
                : false;
              if (isRealtimeModelAfterChange) {
                if (voiceProvider !== VoiceProvider.OpenAi) {
                  setVoiceProvider(VoiceProvider.OpenAi);
                }
                if (selectedLanguage !== VoiceCallLanguage.English) {
                  setSelectedLanguage(VoiceCallLanguage.English);
                }
                if (
                  isDefined(modelTemperature) &&
                  (modelTemperature < 0.5 || modelTemperature > 0.7)
                ) {
                  setModelTemperature(0.5);
                }
                // Reset persona if current selection is not realtime-compatible
                if (voicePersona && !isRealtimeCompatibleVoicePersona(voicePersona)) {
                  setVoicePersona(Object.values(OpenAiStandardVoicePersona)[0]);
                }
              }
            }}
            required
          />
          {oldModelName !== modelName && (
            <Alert variant="warning">
              Selecting a new LLM Model (previously selected &quot;{oldModelName}&quot;) is a major
              change. We recommend thorough testing, as you may need to tune your prompt to optimize
              it for the new model. A prompt that works for one model may not work as well for
              another model.
            </Alert>
          )}
          {isRealtimeModel && (
            <Alert variant="info">
              <Stack gap={1}>
                <Text bold>OpenAI Realtime Models (Beta)</Text>
                <Text>
                  OpenAI&apos;s Realtime models are currently in beta and{' '}
                  <Text bold component="span">
                    not recommended for production deployments at scale.
                  </Text>
                </Text>
                <Text>
                  These models use speech-to-speech processing for ultra-low latency, which can
                  improve conversational quality and handle pronunciation challenges better than
                  text-to-speech alternatives. However, there is a known limitation with tool
                  calling that may cause unexpected behavior.
                </Text>
                <Text>
                  We&apos;re monitoring OpenAI&apos;s progress and will update this notice once the
                  models are production-ready.
                </Text>
                <Text bold>Configuration Requirements:</Text>
                <Stack gap={0.5} sx={{ pl: 2 }}>
                  <Text>• Temperature must be set between 0.5-0.7 (defaults to 0.5)</Text>
                  <Text>• Voice Provider must be set to OpenAI</Text>
                </Stack>
                <Text bold>Important:</Text> Review the prompting guides for realtime and
                non-realtime models:
                <Stack gap={0.5}>
                  <ExternalLink href="https://cookbook.openai.com/examples/realtime_prompting_guide">
                    OpenAI Realtime Prompting Guide
                  </ExternalLink>
                  <ExternalLink href="https://docs.vapi.ai/openai-realtime#prompting-best-practices">
                    Vapi Realtime Best Practices
                  </ExternalLink>
                </Stack>
              </Stack>
            </Alert>
          )}
          {isGpt5Model ? (
            <Alert variant="info">GPT-5 models only support a model temperature of 1.</Alert>
          ) : (
            <BlockTextField
              type="number"
              fieldName="config.modelTemperature"
              label="Model Temperature"
              helperText={TEMPERATURE_HELPER_TEXT}
              required={false}
            />
          )}

          {!isNativelyMultilingual && !isEnglishOnly && (
            <>
              <BlockSwitchField
                fieldName="config.language"
                label={MULTILINGUAL_LABEL}
                toggleStates={{
                  activeState: VoiceCallLanguage.Multilingual,
                  inactiveState: previousLanguage || VoiceCallLanguage.English,
                }}
                helperText={MULTILINGUAL_HELPER_TEXT}
              />
              <BlockSelectField
                fieldName="config.language"
                label={LANGUAGE_LABEL}
                helperText={LANGUAGE_HELPER_TEXT}
                options={languageOptions}
                disabled={selectedLanguage === VoiceCallLanguage.Multilingual}
                onChange={(event) => {
                  const newLanguage = event.target.value as VoiceCallLanguage;
                  if (newLanguage !== VoiceCallLanguage.Multilingual) {
                    setPreviousLanguage(newLanguage);
                  }
                }}
                required
              />
            </>
          )}
          {isNativelyMultilingual && isDefined(voiceProvider) && (
            <Alert variant="info">
              <Text>
                {voiceProvider === VoiceProvider.Cartesia ? 'Cartesia' : 'OpenAI Realtime'} supports
                native multilingual capabilities. All voices can automatically detect and speak in
                multiple languages without language configuration.
              </Text>
            </Alert>
          )}
          <BlockSelectField
            fieldName="config.voiceProvider"
            label="Voice Provider"
            helperText="Select the voice provider. OpenAI is required for Realtime LLM models."
            options={[
              { value: VoiceProvider.ElevenLabs, label: '11Labs (Default)' },
              { value: VoiceProvider.OpenAi, label: 'OpenAI' },
              { value: VoiceProvider.Cartesia, label: 'Cartesia' },
            ]}
            onChange={(event) => {
              const newProvider = event.target.value as VoiceProvider;
              const isRealtimeModelAfterChange = isRealtimeModel;
              const isNativelyMultilingualAfterChange =
                isRealtimeModelAfterChange || newProvider === VoiceProvider.Cartesia;
              const isEnglishOnlyAfterChange =
                isDefined(newProvider) &&
                newProvider === VoiceProvider.OpenAi &&
                !isRealtimeModelAfterChange;

              // For providers that only support English (natively multilingual or English-only),
              // reset language to English if needed
              if (
                (isNativelyMultilingualAfterChange || isEnglishOnlyAfterChange) &&
                selectedLanguage !== VoiceCallLanguage.English
              ) {
                setSelectedLanguage(VoiceCallLanguage.English);
              }
              // Reset persona to default if current selection is incompatible with new provider
              if (voicePersona) {
                const isCompatible =
                  newProvider === VoiceProvider.OpenAi
                    ? isRealtimeModelAfterChange
                      ? isRealtimeCompatibleVoicePersona(voicePersona)
                      : Object.values(OpenAiStandardVoicePersona).includes(
                          voicePersona as OpenAiStandardVoicePersona
                        )
                    : newProvider === VoiceProvider.Cartesia
                      ? isCartesiaCompatibleVoicePersona(voicePersona)
                      : isElevenLabsCompatibleVoicePersona(voicePersona);
                if (!isCompatible) {
                  const defaultPersona =
                    newProvider === VoiceProvider.OpenAi
                      ? isRealtimeModelAfterChange
                        ? OPENAI_REALTIME_COMPATIBLE_VOICES[0]
                        : Object.values(OpenAiStandardVoicePersona)[0]
                      : newProvider === VoiceProvider.Cartesia
                        ? Object.values(CartesiaVoicePersona)[0]
                        : Object.values(ElevenLabsVoicePersona)[0];
                  setVoicePersona(defaultPersona);
                }
              }
            }}
            disabled={voiceProvider === VoiceProvider.OpenAi && isRealtimeModel}
            required
          />
          <BlockSelectField
            fieldName="config.voicePersona"
            label={VOICE_PERSONA_LABEL}
            helperText={VOICE_PERSONA_HELPER_TEXT}
            options={personaOptionsFilteredByLanguage}
            required
          />
          {!isRealtimeModel && (
            <>
              {isDefined(voiceProvider) && voiceProvider === VoiceProvider.ElevenLabs && (
                <>
                  <BlockTextField
                    type="number"
                    fieldName="config.voiceSimilarityBoost"
                    label={VOICE_SIMILARITY_BOOST_LABEL}
                    helperText={ELEVEN_LABS_VOICE_SIMILARITY_BOOST_HELPER_TEXT}
                    required={false}
                  />
                  <BlockTextField
                    type="number"
                    fieldName="config.voiceSpeed"
                    label={VOICE_SPEED_LABEL}
                    helperText={ELEVEN_LABS_VOICE_SPEED_HELPER_TEXT}
                    required={false}
                  />
                  <BlockTextField
                    type="number"
                    fieldName="config.voiceStability"
                    label={VOICE_STABILITY_LABEL}
                    helperText={ELEVEN_LABS_VOICE_STABILITY_HELPER_TEXT}
                    required={false}
                  />
                </>
              )}
              {isDefined(voiceProvider) && voiceProvider === VoiceProvider.Cartesia && (
                <BlockTextField
                  type="number"
                  fieldName="config.voiceSpeed"
                  label={VOICE_SPEED_LABEL}
                  helperText={CARTESIA_VOICE_SPEED_HELPER_TEXT}
                  required={false}
                />
              )}
              <BlockSelectField
                fieldName="config.emotionRecognition"
                label={EMOTION_RECOGNITION_LABEL}
                helperText={EMOTION_RECOGNITION_HELPER_TEXT}
                options={Object.values(VoiceCallOptionMode).map((value) => ({
                  value,
                  label: value,
                }))}
                required={false}
              />
              <BlockSelectField
                fieldName="config.backgroundDenoising"
                label={BACKGROUND_DENOISING_LABEL}
                helperText={BACKGROUND_DENOISING_HELPER_TEXT}
                options={Object.values(VoiceCallOptionMode).map((value) => ({
                  value,
                  label: value,
                }))}
                required={false}
              />
              <BlockSelectField
                fieldName="config.backgroundSound"
                label={BACKGROUND_SOUND_LABEL}
                helperText={BACKGROUND_SOUND_HELPER_TEXT}
                options={Object.values(VoiceCallBackgroundSound).map((value) => ({
                  value,
                  label: _.capitalize(value),
                }))}
                required={false}
              />
              <BlockSelectField
                fieldName="config.startSpeakingSmartEndpointing"
                label={START_SPEAKING_SMART_ENDPOINTING_LABEL}
                helperText={START_SPEAKING_SMART_ENDPOINTING_HELPER_TEXT}
                options={Object.values(VoiceCallOptionMode).map((value) => ({
                  value,
                  label: value,
                }))}
                required={false}
              />
              {isDefined(voiceProvider) && voiceProvider === VoiceProvider.ElevenLabs && (
                <BlockSwitchField
                  fieldName="config.enableSsmlParsing"
                  label="Enable SSML Parsing"
                  helperText={SSML_PARSING_HELPER_TEXT}
                  required={false}
                />
              )}
              <BlockTextField
                type="number"
                fieldName="config.startSpeakingWaitSeconds"
                label={START_SPEAKING_WAIT_SECONDS_LABEL}
                helperText={START_SPEAKING_WAIT_SECONDS_HELPER_TEXT}
                required={false}
              />
            </>
          )}
          <BlockValueTemplateFieldArea
            fieldName="config.endCallMessage"
            label={END_CALL_MESSAGE_LABEL}
            helperText={END_CALL_MESSAGE_HELPER_TEXT}
          />
          <BlockSelectField
            fieldName="config.endCallFunction"
            label={END_CALL_FUNCTION_LABEL}
            helperText={END_CALL_FUNCTION_HELPER_TEXT}
            options={Object.values(VoiceCallOptionMode).map((value) => ({
              value,
              label: value,
            }))}
            required={false}
          />
          <BlockValueTemplateFieldArea
            fieldName="config.voicemailMessage"
            label="Voice Mail Message"
            helperText={VOICEMAIL_MESSAGE_HELPER_TEXT}
          />
          {!isRealtimeModel && (
            <>
              <BlockAutocomplete
                fieldName="config.idleMessages"
                label={IDLE_MESSAGES_LABEL}
                options={Object.values(VoiceCallIdleMessage)}
                multiple={true}
                helperText={IDLE_MESSAGES_HELPER_TEXT}
              />
              <BlockTextField
                type="number"
                fieldName="config.idleMessageMaxSpokenCount"
                label={IDLE_MESSAGE_MAX_SPOKEN_COUNT_LABEL}
                helperText={IDLE_MESSAGE_MAX_SPOKEN_COUNT_HELPER_TEXT}
                required={false}
              />
              <BlockTextField
                type="number"
                fieldName="config.idleTimeoutSeconds"
                label={IDLE_TIMEOUT_SECONDS_LABEL}
                helperText={IDLE_TIMEOUT_SECONDS_HELPER_TEXT}
                required={false}
              />
              <BlockTextField
                type="number"
                fieldName="config.stopSpeakingNumberOfWords"
                label={STOP_SPEAKING_NUMBER_OF_WORDS_LABEL}
                helperText={STOP_SPEAKING_NUMBER_OF_WORDS_HELPER_TEXT}
                required={false}
              />
              <BlockTextField
                type="number"
                fieldName="config.stopSpeakingVoiceSeconds"
                label={STOP_SPEAKING_VOICE_SECONDS_LABEL}
                helperText={STOP_SPEAKING_VOICE_SECONDS_HELPER_TEXT}
                required={false}
              />
              <BlockTextField
                type="number"
                fieldName="config.stopSpeakingVoiceBackOffSeconds"
                label={STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_LABEL}
                helperText={STOP_SPEAKING_VOICE_BACK_OFF_SECONDS_HELPER_TEXT}
                required={false}
              />
            </>
          )}
          <BlockDurationField
            fieldName="config.timeout"
            unitFieldNameSuffix="units"
            required={true}
            allowedUnits={[DurationUnits.Hours, DurationUnits.Minutes, DurationUnits.Seconds]}
            toggleLabel="Block Timeout"
            valueLabel="Block timeout duration"
            valueHelperText={TIMEOUT_HELPER_TEXT}
          />
          <BlockDurationField
            fieldName="config.maxCallDuration"
            unitFieldNameSuffix="units"
            required={true}
            allowedUnits={[DurationUnits.Hours, DurationUnits.Minutes, DurationUnits.Seconds]}
            toggleLabel="Call Duration Limit"
            valueLabel="Maximum call duration"
            valueHelperText={MAX_CALL_DURATION_HELPER_TEXT}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
